import * as React from 'react';
import { type ReactElement, forwardRef } from 'react';

import { CTA, Text, TextAlignment, TokenTextAppearance, WhiteSpace, styled } from '@volkswagen-onehub/components-core';

import { LoadingIconCircle } from '../loading-spinner';

export enum PickerType {
	Color = 'color',
	Rim = 'rim',
	Interior = 'interior'
}

interface Props {
	readonly isActive: boolean;
	readonly label: string;
	readonly ariaLabel: string;
	readonly setActive: () => void;
	readonly isLoading?: boolean;
	readonly testId: string;
	readonly icon?: ReactElement;
	readonly isSpaced?: boolean;
	readonly isSegmented?: boolean;
	readonly isFirstInSegment?: boolean;
}

const Picker = forwardRef<HTMLButtonElement, Props>(
	(
		{ label, isActive, setActive, ariaLabel, icon, testId, isSpaced, isLoading, isSegmented, isFirstInSegment },
		ref
	) => {
		return (
			<StyledPicker
				ref={ref}
				isLoading={isLoading}
				isActive={isActive}
				$isSegmented={isSegmented}
				$isFirstInSegment={isFirstInSegment}
				data-testid={testId}
				tag="button"
				aria-current={isActive}
				aria-label={ariaLabel}
				aria-busy={isLoading}
				emphasis={isActive ? 'primary' : 'secondary'}
				size="small"
				onClick={() => setActive()}
				icon={<>{isLoading && isActive && <LoadingIconCircle pickerLoad />}</>}
			>
				<LabelWrapper
					staticSize
					whiteSpace={WhiteSpace.pre}
					appearance={TokenTextAppearance.copy200}
					textAlign={TextAlignment.center}
					bold={isActive}
					isSpaced={isSpaced ? !icon : false}
					$isSegmented={isSegmented}
					$isActive={isActive}
				>
					{label}
				</LabelWrapper>
			</StyledPicker>
		);
	}
);

Picker.displayName = 'Visualizer_Picker';

const StyledPicker = styled(CTA)<{
	isActive: boolean;
	isLoading?: boolean;
	$isSegmented?: boolean;
	$isFirstInSegment?: boolean;
}>`
	border: 1px solid ${props => props.theme.colors.content.active};
	cursor: ${({ isActive, isLoading }) => {
		if (isActive || isLoading) return 'default';

		return 'pointer';
	}};

	${({ theme, isActive, $isSegmented, $isFirstInSegment }) =>
		$isSegmented &&
		theme.variant === 'v26' &&
		`
			&& {
				border: none;
				border-left: ${$isFirstInSegment ? 'none' : '1px solid #D4CABD'};
				border-radius: 0;
				box-shadow: none;
				min-width: 114px;
				background-color: ${isActive ? '#D9CDBD' : 'transparent'};
				color: ${isActive ? '#1B233D' : '#D9DEE7'};
			}

			&&:hover {
				background-color: ${isActive ? '#D9CDBD' : 'rgba(255, 255, 255, 0.06)'};
			}

			&&:focus-visible {
				outline: none;
				box-shadow: inset 0 0 0 1px #D9CDBD;
			}
		`}
`;

// margin 12 px, why?
// icon is 16 px + size in between label and icon is 8px = 24px
// 24px / 2 to have label in the middle
const LabelWrapper = styled(Text)<{ isSpaced?: boolean; $isSegmented?: boolean; $isActive?: boolean }>`
	margin: ${props => (props.isSpaced ? ' 0 12px' : '0')};
	${({ theme, $isSegmented, $isActive }) =>
		$isSegmented &&
		theme.variant === 'v26' &&
		`
			&& {
				color: ${$isActive ? '#1B2236' : '#D9DEE7'};
			}
		`}
`;

export default Picker;
